import React, { useMemo, useState } from "react";
import cn from "classnames";

import { Input } from "components/Input";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace } from "components/Spacing";

import { printFieldCategories } from "./printFieldCategories";
import styles from "./styles.module.scss";

const formatFieldName = (name) =>
  name.includes(".") || name.includes(" ") ? name : `{{${name}}}`;

/** Справочник переменных шаблонов чеков с поиском и группами */
export const PrintFieldsReference = ({ compact = false, embedded = false }) => {
  const [search, setSearch] = useState("");
  const [openSections, setOpenSections] = useState(() =>
    Object.fromEntries(printFieldCategories.map((c) => [c.title, true]))
  );

  const query = search.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!query) return printFieldCategories;

    return printFieldCategories
      .map((category) => {
        const fields = Object.entries(category.fields).filter(
          ([name, meta]) =>
            name.toLowerCase().includes(query) ||
            meta.type.toLowerCase().includes(query) ||
            meta.description.toLowerCase().includes(query)
        );
        if (!fields.length) return null;
        return { ...category, fields: Object.fromEntries(fields) };
      })
      .filter(Boolean);
  }, [query]);

  const toggleSection = (title) => {
    setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <div className={styles.printFieldsRef}>
      {!compact && (
        <>
          <MediumText size={13}>Справочник полей шаблонов чеков</MediumText>
          <VerticalSpace size={8} />
        </>
      )}
      <RegularText size={12} color="muted">
        Переменные для Mustache-шаблонов и связанные поля печати. Список может
        меняться.
      </RegularText>
      <VerticalSpace size={10} />
      <Input
        placeholder="Поиск по имени, типу или описанию…"
        value={search}
        onChange={(ev) => setSearch(ev.target.value)}
        inputWidth="100%"
      />
      <VerticalSpace size={10} />
      <div
        className={cn(styles.printFieldsList, {
          [styles.printFieldsListEmbedded]: embedded,
        })}
      >
        {filteredCategories.length === 0 && (
          <RegularText size={12} color="muted">
            Ничего не найдено
          </RegularText>
        )}
        {filteredCategories.map((category) => {
          const isOpen = openSections[category.title] ?? true;
          const count = Object.keys(category.fields).length;

          return (
            <div key={category.title} className={styles.printFieldsSection}>
              <button
                type="button"
                className={styles.printFieldsSectionHead}
                onClick={() => toggleSection(category.title)}
              >
                <MediumText size={12}>{category.title}</MediumText>
                <RegularText size={12} color="muted">
                  {isOpen ? "▾" : "▸"} · {count}
                </RegularText>
              </button>
              {isOpen && (
                <div className={styles.printFieldsSectionBody}>
                  {Object.entries(category.fields).map(([name, meta]) => (
                    <div key={name} className={styles.printFieldRow}>
                      <code className={styles.printFieldName}>
                        {formatFieldName(name)}
                      </code>
                      <RegularText size={11} color="muted" className={styles.printFieldType}>
                        {meta.type}
                      </RegularText>
                      <RegularText size={12} className={styles.printFieldDesc}>
                        {meta.description}
                      </RegularText>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
